import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formattedDate, formattedTime } from "@/lib/helper";
import { cn } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { Ban, CircleCheck, MoreHorizontal, MoreVertical } from "lucide-react";
import { Link } from "react-router";
import { VscEye } from "react-icons/vsc";
import Suspend from "./common/Suspend";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { forwardRef, useRef, useState } from "react";
import Unsuspend from "./common/Unsuspend";
import { HiOutlineMail } from "react-icons/hi";
import { ToastMessage, type } from "@/lib/toastr";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { verifyEmail } from "@/services/Users.service";
// This type is used to define the shape of our data.
// You can use a Zod schema here if you want.
export type User = {
  id: string;
  firstName: string;
  lastName: string;
  createdAt: string;
  email: string;
  status: "active" | "inactive";
};
export type Transaction = {
  createdAt: string;
  crypto: string;
  received: string;
  rate: string;
  paid: string;
  reference: string;
  status: "successful" | "pending" | "failed";
};

export const columns: ColumnDef<User>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },

  {
    accessorKey: "fullName",
    header: ({ column }) => {
      return <div>Full Name</div>;
    },
    cell: ({ row }) => (
      <div>
        {row?.original?.firstName} {row?.original?.lastName}
      </div>
    ),
  },
  {
    accessorKey: "email",
    header: ({ column }) => {
      return <div>Email</div>;
    },
    cell: ({ row }) => <div className="lowercase">{row.getValue("email")}</div>,
  },
  {
    accessorKey: "createdAt",
    header: () => <div className="">Date Registered</div>,
    cell: ({ row }) => {
      return (
        <div className="font-medium">
          {formattedDate(row.getValue("createdAt"))}{" "}
          {formattedTime(row.getValue("createdAt"))}
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Account Status",
    cell: ({ row }) => (
      <div
        className={cn(
          "capitalize flex justify-center items-center rounded-[37px] py-1 px-2",
          row.getValue("status") === "active"
            ? "bg-green0 text-green-3"
            : "bg-saffron-10 text-saffron"
        )}
      >
        {row.getValue("status")}
      </div>
    ),
  },
  {
    accessorKey: "kycStatus",
    header: "KYC Status",
    cell: ({ row }) => (
      <div
        className={cn(
          "capitalize flex justify-center items-center rounded-[37px] py-1 px-2",
          row.getValue("kycStatus") === "verified"
            ? "bg-green0 text-green-3"
            : "bg-saffron-10 text-saffron"
        )}
      >
        {row.getValue("kycStatus")}
      </div>
    ),
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      return <DropdownWithDialogItemsSolution2 data={row?.original} />;
    },
  },
];
export const columnsReferrals: ColumnDef<User>[] = [
  {
    accessorKey: "fullName",
    header: ({ column }) => {
      return <div>Full Name</div>;
    },
    cell: ({ row }) => (
      <div>
        {row?.original?.firstName} {row?.original?.lastName}
      </div>
    ),
  },
  {
    accessorKey: "email",
    header: ({ column }) => {
      return <div>Email</div>;
    },
    cell: ({ row }) => <div className="lowercase">{row.getValue("email")}</div>,
  },
  {
    accessorKey: "createdAt",
    header: () => <div className="">Date Registered</div>,
    cell: ({ row }) => {
      return (
        <div className="font-medium">
          {formattedDate(row.getValue("createdAt"))}{" "}
          {formattedTime(row.getValue("createdAt"))}
        </div>
      );
    },
  },
  {
    accessorKey: "kycStatus",
    header: "Status",
    cell: ({ row }) => (
      <div
        className={cn(
          "capitalize flex justify-center items-center rounded-[37px] py-1 px-2",
          row.getValue("kycStatus") === "verified"
            ? "bg-green0 text-green-3"
            : "bg-saffron-10 text-saffron"
        )}
      >
        {row.getValue("kycStatus")}
      </div>
    ),
  },
];
export const columnsT: ColumnDef<User>[] = [
  {
    accessorKey: "fullName",
    header: ({ column }) => {
      return <div>Full Name</div>;
    },
    cell: ({ row }) => (
      <div>
        {row?.original?.merchant?.firstName} {row?.original?.merchant?.lastName}
      </div>
    ),
  },
  {
    accessorKey: "roleId",
    header: ({ column }) => {
      return <div>Role</div>;
    },
    cell: ({ row }) => (
      <div className="capitalize">{row?.original?.roleId?.split("-")[1]}</div>
    ),
  },
  {
    accessorKey: "email",
    header: ({ column }) => {
      return <div>Email</div>;
    },
    cell: ({ row }) => (
      <div className="lowercase">{row?.original?.merchant?.email}</div>
    ),
  },
  {
    accessorKey: "phone",
    header: () => <div className="">Phone Number</div>,
    cell: ({ row }) => {
      return <div className="">{row.original.merchant.phone}</div>;
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <div
        className={cn(
          "capitalize flex justify-center items-center rounded-[37px] py-1 px-2",
          row.getValue("status") === "active"
            ? "bg-green0 text-green-3"
            : "bg-saffron-10 text-saffron"
        )}
      >
        {row.getValue("status")}
      </div>
    ),
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-8 h-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Link
                to={row?.original?.id}
                className="flex items-center gap-2 text-xs text-primary-9"
              >
                <VscEye />
                View Profile
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

export const columnsTrans: ColumnDef<Transaction>[] = [
  {
    accessorKey: "createdAt",
    header: ({ column }) => {
      return <div>Date</div>;
    },
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="text-sm text-gray">
          {formattedDate(row.getValue("createdAt"))}
        </span>
        <span className="text-sm text-gray-0">
          {formattedTime(row.getValue("createdAt"))}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "currency",
    header: ({ column }) => {
      return <div>Crypto</div>;
    },
    cell: ({ row }) => <div>{row?.getValue("currency")}</div>,
  },
  {
    accessorKey: "amount",
    header: ({ column }) => {
      return <div>We received</div>;
    },
    cell: ({ row }) => (
      <div>
        {row.getValue("amount")} {row?.getValue("currency")}
      </div>
    ),
  },
  {
    accessorKey: "rate",
    header: () => <div className="capitalize">Exchange Rate</div>,
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue("rate"));
      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "NGN",
      }).format(amount);

      return (
        <div className="font-medium">
          1 {row?.getValue("currency")} / {formatted}{" "}
        </div>
      );
    },
  },
  {
    accessorKey: "withdrawalAmount",
    header: () => <div className="capitalize">We Paid out</div>,
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue("withdrawalAmount"));

      const formatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "NGN",
      }).format(amount);

      return <div className="font-medium">{formatted} </div>;
    },
  },
  {
    accessorKey: "Reference No.",
    header: () => <div className="capitalize">Reference No.</div>,
    cell: ({ row }) => {
      return <div className="">{row.original?.reference} </div>;
    },
  },
  {
    accessorKey: "status",
    header: () => <div className="capitalize">General Status</div>,
    cell: ({ row }) => (
      <div
        className={cn(
          "flex justify-center items-center rounded-[4px]  py-1 w-fit px-8 capitalize",
          row.getValue("status") === "success"
            ? "bg-green-0 text-green-3"
            : row.getValue("status") === "pending"
              ? "bg-saffron-10 text-saffron"
              : "bg-error-0 text-error-2"
        )}
      >
        <p className="font-medium text-xs">{row.getValue("status")}</p>
      </div>
    ),
  },
  {
    accessorKey: "_status",
    header: () => <div className="capitalize">Platform Status</div>,
    cell: ({ row }) => (
      <div
        className={cn(
          "flex justify-center items-center rounded-[4px]  py-1 w-fit px-8 capitalize",
          row.getValue("_status") === "blocked" ||
            row.getValue("_status") === "failed" ||
            row.getValue("_status") === "cancelled"
            ? "bg-error-0 text-error-2"
            : row.getValue("_status") === "pending_withdrawal" ||
              row.getValue("_status") === "pending_swap" ||
              row.getValue("_status") === "pending_payin" ||
              row.getValue("_status") === "pending_settlement" ||
              row.getValue("_status") === "processing"
              ? "bg-saffron-10 text-saffron"
              : row?.getValue("_status") === "queued"
                ? "bg-blue-400 text-blue-700"
                : "bg-green-0 text-green-3"
        )}
      >
        <p className="font-medium text-xs">{row.getValue("_status")}</p>
      </div>
    ),
  },
  {
    id: "actions",
    enableHiding: false,
    cell: ({ row }) => {
      const d = row.original;

      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="w-8 h-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem>
              <Link
                to={row?.original?.id}
                className="flex items-center gap-2 text-xs text-primary-9"
              >
                <VscEye />
                View Transaction
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];

function DropdownWithDialogItemsSolution2({ data }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasOpenDialog, setHasOpenDialog] = useState(false);
  const dropdownTriggerRef = useRef(null);
  const focusRef = useRef(null);
  const queryClient = useQueryClient();
  const mutationVerify = useMutation({
    mutationFn: async () => await verifyEmail(data?.id),
    mutationKey: ["verifyEmail"],
    onSuccess: () => {
      queryClient.clear();

      ToastMessage(type.SUCCESS, "Customer email verified");
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  function handleDialogItemSelect() {
    focusRef.current = dropdownTriggerRef.current;
  }

  function handleDialogItemOpenChange(open) {
    setHasOpenDialog(open);
    if (open === false) {
      setDropdownOpen(false);
    }
  }
  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="w-8 h-8 p-0"
          ref={dropdownTriggerRef}
        >
          <span className="sr-only">Open menu</span> <MoreVertical />{" "}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        sideOffset={5}
        hidden={hasOpenDialog}
        onCloseAutoFocus={(event) => {
          if (focusRef.current) {
            focusRef.current.focus();
            focusRef.current = null;
            event.preventDefault();
          }
        }}
      >
        <DropdownMenuItem className="cursor-pointer">
          <Link
            to={data?.id}
            className="flex items-center gap-2 text-xs text-primary-9"
          >
            <VscEye />
            View Profile
          </Link>
        </DropdownMenuItem>
        {data?.kycStatus === "unverified" && (
          <DropdownMenuItem
            onClick={() => mutationVerify.mutate()}
            className="flex items-center gap-2 text-xs text-primary-9"
          >
            <HiOutlineMail />
            Verify Email
          </DropdownMenuItem>
        )}

        <DialogItem
          triggerChildren={
            <>
              {data?.status !== "active" ? (
                <p className="flex items-center gap-2 text-xs text-green-3 hover:text-green-3">
                  <CircleCheck /> Unsuspend
                </p>
              ) : (
                <p className="flex items-center gap-2 text-xs text-error hover:text-error">
                  <Ban /> Suspend
                </p>
              )}
            </>
          }
          onSelect={handleDialogItemSelect}
          onOpenChange={handleDialogItemOpenChange}
          size="sm"
        >
          {data?.status === "active" ? (
            <Suspend
              id={data?.id}
              name={`${data?.firstName} ${data?.lastName}`}
            />
          ) : (
            <Unsuspend
              id={data?.id}
              name={`${data?.firstName} ${data?.lastName}`}
            />
          )}
        </DialogItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const DialogItem = forwardRef((props, forwardedRef) => {
  const {
    triggerChildren,
    children,
    onSelect,
    onOpenChange,
    size,
    ...itemProps
  } = props;
  return (
    <Dialog onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          {...itemProps}
          ref={forwardedRef}
          className="cursor-pointer"
          onSelect={(event) => {
            event.preventDefault();
            onSelect && onSelect();
          }}
        >
          {triggerChildren}
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent
        className={cn(
          "p-0 text-left items-start rounded-[4px] bg-background",
          size ? `max-w-${size}` : "max-w-xs"
        )}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
});
